import React from "react";
import ItemList from "@imminently/decisively-ui/ItemList";

import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
} from "../components/ComponentPage";

const items = Array.from({ length: 1000 }, (_, index) => ({
  id: index + 1,
  title: `Scenario ${index + 1}`,
  status: index % 3 === 0 ? "Ready" : index % 3 === 1 ? "Running" : "Complete",
}));

export function ItemListPage() {
  const [smallCount, setSmallCount] = React.useState(10);
  const [largeCount, setLargeCount] = React.useState(30);
  const [isSmallLoadingMore, setIsSmallLoadingMore] = React.useState(false);
  const [isLargeLoadingMore, setIsLargeLoadingMore] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [skeletonCount, setSkeletonCount] = React.useState(10);
  const getItemAt = React.useCallback((index: number) => {
    const base = items[index % items.length];
    return {
      ...base,
      id: index + 1,
      title: `Scenario ${index + 1}`,
    };
  }, []);

  const smallItems = React.useMemo(
    () => Array.from({ length: smallCount }, (_, index) => getItemAt(index)),
    [smallCount, getItemAt],
  );
  const largeItems = React.useMemo(
    () => Array.from({ length: largeCount }, (_, index) => getItemAt(index)),
    [largeCount, getItemAt],
  );
  const skeletonItems = React.useMemo(
    () => Array.from({ length: skeletonCount }, (_, index) => getItemAt(index)),
    [skeletonCount, getItemAt],
  );

  return (
    <ComponentPage
      title="Item List"
      description="Displays a vertical list of selectable items with built-in reach-end loading support."
    >
      <ComponentSection
        title="Reach End (Below 25)"
        description="Uses onReachEnd with fewer than 25 items."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <div className="h-[420px] w-full max-w-md rounded-md border border-border">
            <ItemList
              header="Scenarios"
              items={smallItems}
              getItemKey={(item) => item.id}
              hasMore={true}
              loadingMore={isSmallLoadingMore}
              onReachEnd={() => {
                if (isSmallLoadingMore) return;
                setIsSmallLoadingMore(true);
                setTimeout(() => {
                  setSmallCount((v) => v + 10);
                  setIsSmallLoadingMore(false);
                }, 700);
              }}
              renderItem={(item) => (
                <ItemList.Item key={item.id}>
                  <ItemList.Left>
                    <ItemList.Title>{item.title}</ItemList.Title>
                    <div className="text-xs text-muted-foreground">{item.status}</div>
                  </ItemList.Left>
                  <ItemList.Right>
                    <span className="text-xs text-muted-foreground">#{item.id}</span>
                  </ItemList.Right>
                </ItemList.Item>
              )}
            />
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Reach End (Over 25)"
        description="Starts over 25 items so you can verify load-more behavior under virtualization."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <div className="h-[420px] w-full max-w-md rounded-md border border-border">
            <ItemList
              header="Scenarios"
              items={largeItems}
              getItemKey={(item) => item.id}
              hasMore={true}
              loadingMore={isLargeLoadingMore}
              onReachEnd={() => {
                if (isLargeLoadingMore) return;
                setIsLargeLoadingMore(true);
                setTimeout(() => {
                  setLargeCount((v) => v + 25);
                  setIsLargeLoadingMore(false);
                }, 700);
              }}
              renderItem={(item) => (
                <ItemList.Item key={item.id}>
                  <ItemList.Left>
                    <ItemList.Title>{item.title}</ItemList.Title>
                    <div className="text-xs text-muted-foreground">{item.status}</div>
                  </ItemList.Left>
                  <ItemList.Right>
                    <span className="text-xs text-muted-foreground">#{item.id}</span>
                  </ItemList.Right>
                </ItemList.Item>
              )}
            />
          </div>
        </ComponentPreview>
        <pre className="overflow-auto rounded-md bg-muted p-4 text-xs">
{`<ItemList
  items={items}
  getItemKey={(item) => item.id}
  hasMore={hasMore}
  loadingMore={loadingMore}
  onReachEnd={loadMore}
  renderItem={(item) => <ItemList.Item key={item.id}>...</ItemList.Item>}
/>`}
        </pre>
      </ComponentSection>

      <ComponentSection
        title="Skeleton Rows"
        description="Shows appended skeleton rows via `skeletonRows` and `renderSkeletonItem`."
      >
        <ComponentPreview className="items-stretch justify-stretch">
          <div className="h-[420px] w-full max-w-md rounded-md border border-border">
            <ItemList
              header="Scenarios"
              items={skeletonItems}
              getItemKey={(item) => item.id}
              hasMore={true}
              loadingMore={isLoadingMore}
              skeletonRows={isLoadingMore ? 5 : 0}
              renderSkeletonItem={(index) => (
                <div className="border-b border-border px-4 py-3" key={`skeleton-${index}`}>
                  <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="mt-2 h-3 w-1/3 animate-pulse rounded bg-muted" />
                </div>
              )}
              onReachEnd={() => {
                if (isLoadingMore) return;
                setIsLoadingMore(true);
                setTimeout(() => {
                  setSkeletonCount((v) => v + 10);
                  setIsLoadingMore(false);
                }, 700);
              }}
              renderItem={(item) => (
                <ItemList.Item key={item.id}>
                  <ItemList.Left>
                    <ItemList.Title>{item.title}</ItemList.Title>
                    <div className="text-xs text-muted-foreground">{item.status}</div>
                  </ItemList.Left>
                  <ItemList.Right>
                    <span className="text-xs text-muted-foreground">#{item.id}</span>
                  </ItemList.Right>
                </ItemList.Item>
              )}
            />
          </div>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
